// Cave84 — canonical human labels for Bottle enums.
// These maps were previously copy-pasted across AddBottlePage / BottleDetailPage /
// InventoryPage / WishlistPage. New code imports from here.

import type {
  BottleFormat, BottleStatus, FillLevel, LabelCondition, PurchaseSource,
} from '@cave84/shared-types';

export const FORMAT_LABELS: Record<BottleFormat, string> = {
  half: 'Half (375 ml)',
  '750ml': '750 ml',
  magnum: 'Magnum (1.5 L)',
  double_magnum: 'Double Magnum (3 L)',
  jeroboam: 'Jeroboam (4.5 L)',
  imperial: 'Impériale (6 L)',
  salmanazar: 'Salmanazar (9 L)',
  methuselah: 'Methuselah (6 L)',
  other: 'Other format',
};

export const FILL_LABELS: Record<FillLevel, string> = {
  into_neck: 'Into neck',
  base_of_neck: 'Base of neck',
  top_shoulder: 'Top shoulder',
  upper_shoulder: 'Upper shoulder',
  mid_shoulder: 'Mid shoulder',
  low_shoulder: 'Low shoulder',
};

export const CONDITION_LABELS: Record<LabelCondition, string> = {
  perfect: 'Perfect',
  minor: 'Minor scuffs',
  scuffed: 'Scuffed',
  torn: 'Torn',
  stained: 'Stained',
  missing: 'Missing',
};

// Single label map for purchase sources — AddBottle, EditBottle, Wishlist,
// BottleDetail and WineAnalysis all read it, so a new source is added once.
export const SOURCE_LABELS: Record<PurchaseSource, string> = {
  mwc_auction: 'MWC Auction',
  tomer_gal: 'Tomer Gal',
  burgundy_crown: 'Burgundy Crown',
  acker: 'Acker',
  idealwine: 'iDealwine',
  sothebys: "Sotheby's",
  zachys: 'Zachys',
  hart_davis_hart: 'Hart Davis Hart',
  retail: 'Retail',
  gift: 'Gift',
  en_primeur: 'En Primeur',
  direct_domaine: 'Direct / Domaine',
  restaurant: 'Restaurant',
  trade: 'Trade / Exchange',
  other: 'Other',
};

export const STATUS_LABELS: Record<BottleStatus, string> = {
  in_cellar: 'In cellar',
  consumed: 'Consumed',
  gifted: 'Gifted',
  sold: 'Sold',
  lost: 'Lost',
  broken: 'Broken',
  trading: 'Trading',
  virtual_pending: 'Pending delivery',
};

/**
 * Fill levels that warrant a visible warning chip. Anything above
 * `top_shoulder` is normal for a mature bottle and would just be noise.
 */
const CONCERNING_FILLS = new Set<FillLevel>([
  'upper_shoulder', 'mid_shoulder', 'low_shoulder',
]);

export function isConcerningFill(f: FillLevel): boolean {
  return CONCERNING_FILLS.has(f);
}

/** Label conditions worth surfacing — `perfect` and `minor` are the default state. */
export function isConcerningCondition(c: LabelCondition): boolean {
  return c !== 'perfect' && c !== 'minor';
}
